import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Calendar, TrendingUp, TrendingDown, Minus, Smile, Frown, Meh } from 'lucide-react';
import { checkinAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';

const MoodTrendAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, all
  const [moodData, setMoodData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    averageMood: 0,
    trend: 'stable',
    bestDay: null,
    lowestDay: null,
    totalEntries: 0
  });

  const timeRanges = [
    { value: '7d', label: 'Week', icon: Calendar },
    { value: '30d', label: 'Month', icon: Calendar },
    { value: '90d', label: '3 Months', icon: Calendar },
    { value: 'all', label: 'All Time', icon: TrendingUp }
  ];

  useEffect(() => {
    loadMoodData();
  }, [timeRange]);

  const loadMoodData = async () => {
    setLoading(true);
    try {
      let data = [];
      
      if (timeRange === 'all') {
        // Get all mood history
        const response = await checkinAPI.getMoodHistory();
        data = response.data.data || [];
      } else {
        // Get stats for specific period
        const response = await checkinAPI.getStats(timeRange);
        data = response.data.data?.mood_trend || [];
      }
      
      setMoodData(data);
      calculateStats(data);
    } catch (error) {
      console.error('Failed to load mood data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    if (data.length === 0) {
      setStats({
        averageMood: 0,
        trend: 'stable',
        bestDay: null,
        lowestDay: null,
        totalEntries: 0
      });
      return;
    }

    const moods = data.map(d => d.mood);
    const avgMood = moods.reduce((a, b) => a + b, 0) / moods.length;
    
    // Find best and lowest days
    const maxMood = Math.max(...moods);
    const minMood = Math.min(...moods);
    const bestDay = data.find(d => d.mood === maxMood);
    const lowestDay = data.find(d => d.mood === minMood);

    // Calculate trend using first half vs second half
    const midPoint = Math.floor(data.length / 2);
    const firstHalf = moods.slice(0, midPoint);
    const secondHalf = moods.slice(midPoint);
    const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length || 0;
    const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length || 0;
    
    let trend = 'stable';
    if (secondAvg > firstAvg + 0.3) trend = 'improving';
    else if (secondAvg < firstAvg - 0.3) trend = 'declining';

    setStats({
      averageMood: avgMood.toFixed(1),
      trend,
      bestDay,
      lowestDay,
      totalEntries: data.length
    });
  };

  // Transform data for chart
  const chartData = moodData.map((item, index) => {
    const date = new Date(item.date);
    let label;
    
    if (timeRange === '7d') {
      label = date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (timeRange === '30d') {
      label = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    } else {
      label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    return {
      date: label,
      fullDate: item.date,
      mood: item.mood,
      stress: item.stress || 0,
      anxiety: item.anxiety || 0,
      energy: item.energy || 0
    };
  });

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getMoodIcon = (mood) => {
    if (mood >= 7) return <Smile className="w-5 h-5 text-green-500" />;
    if (mood >= 4) return <Meh className="w-5 h-5 text-yellow-500" />;
    return <Frown className="w-5 h-5 text-red-500" />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 text-white p-3 rounded-lg shadow-xl">
          <p className="text-sm font-medium mb-1">{data.fullDate}</p>
          <div className="space-y-1">
            <p className="text-sm">😊 Mood: {data.mood}/10</p>
            {data.stress > 0 && <p className="text-sm">😰 Stress: {data.stress}/10</p>}
            {data.anxiety > 0 && <p className="text-sm">😟 Anxiety: {data.anxiety}/10</p>}
            {data.energy > 0 && <p className="text-sm">⚡ Energy: {data.energy}/10</p>}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Mood Trend Over Time</h2>
          <p className="text-gray-600 mt-1">Track your emotional journey based on daily check-ins</p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex gap-2 mt-4 md:mt-0">
          {timeRanges.map((range) => {
            const Icon = range.icon;
            return (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {range.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Average Mood</p>
          <div className="flex items-center">
            {getMoodIcon(parseFloat(stats.averageMood))}
            <span className="text-2xl font-bold text-gray-800 ml-2">{stats.averageMood}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">out of 10</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Trend</p>
          <div className="flex items-center">
            {getTrendIcon(stats.trend)}
            <span className="text-lg font-bold text-gray-800 ml-2 capitalize">{stats.trend}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">over time</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Best Day</p>
          <div className="flex items-center">
            <Smile className="w-5 h-5 text-green-500" />
            <span className="text-lg font-bold text-gray-800 ml-2">
              {stats.bestDay ? stats.bestDay.mood : '-'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 truncate">
            {stats.bestDay ? new Date(stats.bestDay.date).toLocaleDateString() : 'No data'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Entries</p>
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-orange-500" />
            <span className="text-2xl font-bold text-gray-800 ml-2">{stats.totalEntries}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">check-ins</p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                domain={[0, 10]}
                ticks={[0, 2, 4, 6, 8, 10]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="mood"
                stroke="#3b82f6"
                strokeWidth={3}
                fill="url(#moodGradient)"
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
            <div className="text-center">
              <Smile className="w-16 h-16 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No mood data available for this period</p>
              <p className="text-gray-400 text-sm mt-1">Complete daily check-ins to see your mood trends</p>
            </div>
          </div>
        )}
      </div>

      {/* Insights */}
      {stats.totalEntries > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">💡 Mood Insights</h4>
          <p className="text-blue-700 text-sm">
            {stats.trend === 'improving' 
              ? "Your mood has been improving! Keep up the positive momentum with regular check-ins and healthy habits."
              : stats.trend === 'declining'
              ? "Your mood shows a declining trend. Consider reaching out to a support person or trying stress-relief activities."
              : "Your mood has been stable. Consistency is key - maintain your healthy routines!"}
          </p>
        </div>
      )}
    </div>
  );
};

export default MoodTrendAnalytics;
